/**
 * Atualiza <title>, meta description, Open Graph e canonical no cliente (SPA).
 * Busca indexação ainda depende de conteúdo + tempo; isso melhora snippet e compartilhamento.
 */

const DEFAULT_TITLE = 'TripDay | Imóveis para Temporada'
const DEFAULT_DESCRIPTION =
  'TripDay é uma plataforma para encontrar e gerenciar imóveis para temporada, viagem e férias. Veja disponibilidade em um calendário simples e fale direto com o anfitrião.'
const OG_DESCRIPTION_FALLBACK =
  'Encontre e gerencie imóveis para temporada, viagem e férias. Veja disponibilidade e fale direto com o anfitrião.'

const ABOUT_TITLE = 'Quem somos | TripDay'
const ABOUT_DESCRIPTION =
  'Conheça a TripDay: plataforma para anfitriões e imobiliárias gerenciarem imóveis para temporada e para viajantes encontrarem seu próximo destino.'

/** Títulos da área logada (evita indexar painel; foco em uso interno) */
const LOGGED_IN_ROUTE_TITLE = {
  dashboard: 'Painel',
  people: 'Pessoas',
  'person-invites': 'Convites de cadastro',
  properties: 'Imóveis',
  bookings: 'Reservas',
  analysis: 'Dashboard',
  availabilities: 'Disponibilidades',
  profile: 'Perfil',
  'tenant-settings': 'Ambiente',
  users: 'Usuários',
  'service-types': 'Tipos de serviço',
  expenses: 'Despesas',
  about: 'Quem somos'
}

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLinkRel(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function removeMetaByName(name) {
  document.head.querySelectorAll(`meta[name="${name}"]`).forEach((el) => el.remove())
}

export function absolutePageUrl(pathname = window.location.pathname) {
  const origin = window.location.origin
  const path = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname
  if (!path || path === '') return `${origin}/`
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`
}

export function truncateText(str, max) {
  if (!str) return ''
  const t = str.trim()
  if (t.length <= max) return t
  return `${t.slice(0, max - 1).trim()}…`
}

function stripHtml(html) {
  if (!html) return ''
  const d = document.createElement('div')
  d.innerHTML = html
  return (d.textContent || '').replace(/\s+/g, ' ').trim()
}

function defaultOgImageUrl() {
  return `${window.location.origin}/tripday-logo.png`
}

/**
 * Páginas públicas (home / vitrine geral / tenant na raiz)
 */
export function applyDefaultPageMeta() {
  removeMetaByName('robots')

  document.title = DEFAULT_TITLE
  upsertMeta('name', 'description', DEFAULT_DESCRIPTION)

  upsertMeta('property', 'og:title', DEFAULT_TITLE)
  upsertMeta('property', 'og:description', OG_DESCRIPTION_FALLBACK)
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'TripDay')

  const url = absolutePageUrl()
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', defaultOgImageUrl())

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', DEFAULT_TITLE)
  upsertMeta('name', 'twitter:description', truncateText(OG_DESCRIPTION_FALLBACK, 200))

  upsertLinkRel('canonical', url)
}

export function applyAboutPageMeta() {
  removeMetaByName('robots')

  document.title = ABOUT_TITLE
  upsertMeta('name', 'description', ABOUT_DESCRIPTION)

  upsertMeta('property', 'og:title', ABOUT_TITLE)
  upsertMeta('property', 'og:description', truncateText(ABOUT_DESCRIPTION, 200))
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'TripDay')

  const url = absolutePageUrl()
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', defaultOgImageUrl())

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', ABOUT_TITLE)
  upsertMeta('name', 'twitter:description', truncateText(ABOUT_DESCRIPTION, 200))

  upsertLinkRel('canonical', url)
}

export function applyAdminPageMeta() {
  const title = 'Administração | TripDay'
  document.title = title
  upsertMeta('name', 'robots', 'noindex, nofollow')
  upsertMeta('name', 'description', 'Área administrativa TripDay.')

  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', 'Área administrativa TripDay.')
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'TripDay')

  const url = absolutePageUrl()
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', defaultOgImageUrl())

  upsertLinkRel('canonical', url)
}

export function applyLoggedInPageMeta(routeKey) {
  const label = LOGGED_IN_ROUTE_TITLE[routeKey] || 'Painel'
  const title = `${label} | TripDay`
  document.title = title

  upsertMeta('name', 'robots', 'noindex, nofollow')
  upsertMeta('name', 'description', `Área do anfitrião TripDay — ${label}.`)

  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', `Gestão de temporada — ${label}.`)
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'TripDay')

  const url = absolutePageUrl()
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', defaultOgImageUrl())

  upsertLinkRel('canonical', url)
}

/** Enquanto a API do imóvel carrega (URL já correta) */
export function applyPropertySkeletonMeta() {
  removeMetaByName('robots')

  const title = 'Imóvel | TripDay'
  document.title = title
  const desc = 'Detalhes do imóvel para temporada na TripDay — disponibilidade e contato com o anfitrião.'
  upsertMeta('name', 'description', desc)

  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', desc)
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'TripDay')

  const url = absolutePageUrl()
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', defaultOgImageUrl())

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', title)
  upsertMeta('name', 'twitter:description', desc)

  upsertLinkRel('canonical', url)
}

export function applyPropertyPageMeta(property) {
  if (!property) return

  removeMetaByName('robots')

  const locationParts = [property.neighborhood, property.city, property.state].filter(Boolean)
  const locationStr = locationParts.join(', ')
  const title = locationStr
    ? `${property.name} · ${locationStr} | TripDay`
    : `${property.name} | TripDay`

  const rawDesc =
    stripHtml(property.description) ||
    `${property.name}${locationStr ? ` em ${locationStr}` : ''} — aluguel por temporada na TripDay.`
  const description = truncateText(rawDesc, 160)

  document.title = title
  upsertMeta('name', 'description', description)

  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', truncateText(rawDesc, 200))
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'TripDay')

  const url = absolutePageUrl()
  upsertMeta('property', 'og:url', url)

  const image = property.images?.[0]
  if (image) {
    upsertMeta('property', 'og:image', image)
    upsertMeta('name', 'twitter:image', image)
  } else {
    upsertMeta('property', 'og:image', defaultOgImageUrl())
    removeMetaByName('twitter:image')
  }

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', title)
  upsertMeta('name', 'twitter:description', truncateText(rawDesc, 200))

  upsertLinkRel('canonical', url)
}

export function applyPropertyErrorMeta() {
  removeMetaByName('robots')

  const title = 'Imóvel não encontrado | TripDay'
  document.title = title
  const desc = 'Este imóvel não está disponível ou não existe na vitrine TripDay.'
  upsertMeta('name', 'description', desc)

  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', desc)
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'TripDay')

  const url = absolutePageUrl()
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', defaultOgImageUrl())

  upsertLinkRel('canonical', url)
}

/**
 * Ao sair da ficha do imóvel, volta meta conforme rota atual (já atualizada no router).
 */
export function applyMetaAfterPropertyDetailClosed(isLoggedIn, currentRoute) {
  if (currentRoute === 'property-detail') return

  if (isLoggedIn) {
    applyLoggedInPageMeta(currentRoute)
    return
  }

  if (currentRoute === 'about') {
    applyAboutPageMeta()
    return
  }

  if (currentRoute === 'admin') {
    applyAdminPageMeta()
    return
  }

  applyDefaultPageMeta()
}
